import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedStyle } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import {
  usePickDirectedStyleMutation,
  useWriteDirectedScriptMutation,
  useWriteDirectedStylesMutation,
} from '../directedVideoApi';
import { castDescriptionMessages } from '../messages/castDescriptionMessages';
import { storyMessages as messages } from '../messages/storyMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { AgainBox } from './director/AgainBox';
import {
  choicesKey,
  initialChoiceId,
  initialDrafts,
  isOwnStyleValid,
  newOwnStyleId,
  pickedStyleCastFit,
  sameStyle,
  trimmedStyle,
} from './director/choiceRules';
import { DirectedSummary } from './director/DirectedSummary';
import { DirectorNotices } from './director/DirectorNotices';
import { useDirectedAction } from './director/useDirectedAction';
import { DirectorWorking } from './DirectorWorking';
import { StyleCard } from './StyleCard';
import { StyleEditor } from './StyleEditor';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

function emptyOwnStyle(): DirectedStyle {
  return {
    id: newOwnStyleId(),
    title: '',
    camera: '',
    colourAndLight: '',
    texture: '',
    pacing: '',
    effectFamilies: ['lyric', 'title'],
  };
}

/** The choices of one director run. A new run gives a new key, so the edits of the old styles go. */
const StyleChoicesComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, catalogItemUuid, video, song, screen, currentScreen, readOnly, openStep } =
    useDirectedVideoContext();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: video.uuid }),
    [accountId, catalogItemUuid, video.uuid]
  );
  const choices = useMemo(() => video.styles ?? [], [video.styles]);
  // The preview still of each style that the API holds, by style id (owner 2026-10-19).
  const previews = useMemo(
    () => new Map((video.stylePreviews ?? []).map((preview) => [preview.styleId, preview])),
    [video.stylePreviews]
  );
  const [pickStyle] = usePickDirectedStyleMutation();
  const [writeScript] = useWriteDirectedScriptMutation();
  const [writeStyles] = useWriteDirectedStylesMutation();
  const action = useDirectedAction();
  const storedOwn = video.style !== null && !choices.some((style) => style.id === video.style?.id) ? video.style : null;
  const [own, setOwn] = useState<DirectedStyle | null>(storedOwn);
  const [selectedId, setSelectedId] = useState(() => initialChoiceId(choices, video.style));
  const [drafts, setDrafts] = useState<Record<string, DirectedStyle>>(() =>
    initialDrafts(choices, video.style, sameStyle)
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [beforeEdit, setBeforeEdit] = useState<DirectedStyle | null>(null);
  const [isAsking, setIsAsking] = useState(false);

  const isWorking = video.directorState === 'working';
  const noRuns = video.directorRunsLeft <= 0;
  const busy = readOnly || isWorking || action.isRunning;
  const isRevisit = screen !== currentScreen;
  const shownOf = (style: DirectedStyle) => drafts[style.id] ?? style;
  const original = choices.find((style) => style.id === selectedId);
  let picked: DirectedStyle | null = null;
  if (original !== undefined) {
    picked = shownOf(original);
  } else if (own !== null && own.id === selectedId) {
    picked = own;
  }
  const canUse = picked !== null && isOwnStyleValid(picked) && editingId === null;
  // A style that does not fit the art style of the cast pictures gets a warning line (fix 2026-10-18).
  const castFit = pickedStyleCastFit(picked, original, video.cast);
  const isUnchanged = picked !== null && video.style !== null && sameStyle(trimmedStyle(picked), video.style);

  const writeAgain = (note?: string) => {
    action.run(() => writeStyles({ ...args, note }).unwrap()).catch(console.error);
  };

  const applyStyle = async () => {
    setIsAsking(false);
    if (picked === null) {
      return;
    }
    if (isUnchanged && video.script !== null) {
      openStep(null);
      return;
    }
    const style = trimmedStyle(picked);
    const ok = await action.run(async () => {
      await pickStyle({ ...args, style }).unwrap();
      await writeScript(args).unwrap();
    });
    if (ok && isRevisit) {
      openStep(null);
    }
  };

  const onUse = () => {
    if (!isUnchanged && video.script !== null) {
      setIsAsking(true);
      return;
    }
    applyStyle().catch(console.error);
  };

  const startEdit = (style: DirectedStyle) => {
    setSelectedId(style.id);
    setBeforeEdit(style);
    setEditingId(style.id);
  };
  const changeEdited = (next: DirectedStyle) => {
    if (own !== null && next.id === own.id) {
      setOwn(next);
      return;
    }
    setDrafts((current) => ({ ...current, [next.id]: next }));
  };
  /**
   * A family chip of a card: the card is picked with its new families. "Use this style" sends them;
   * with a script, the "Change the style?" question comes first (the rule of every style change).
   */
  const switchFamilies = (next: DirectedStyle) => {
    setSelectedId(next.id);
    changeEdited(next);
  };
  const cancelEdit = () => {
    if (beforeEdit !== null) {
      changeEdited(beforeEdit);
      if (own !== null && beforeEdit.id === own.id && !isOwnStyleValid(beforeEdit)) {
        setOwn(null);
        setSelectedId(initialChoiceId(choices, null));
      }
    }
    setEditingId(null);
  };

  const ownNumber = intl.formatMessage(messages.yourOwn);
  const isEditing = editingId !== null;
  let editor: React.ReactNode = null;
  if (isEditing) {
    const edited = own !== null && own.id === editingId ? own : choices.find((style) => style.id === editingId);
    if (edited !== undefined) {
      const index = choices.findIndex((style) => style.id === editingId);
      editor = (
        <StyleEditor
          style={own !== null && own.id === editingId ? own : shownOf(edited)}
          label={index < 0 ? ownNumber : intl.formatMessage(messages.styleNumber, { number: index + 1 })}
          onChange={changeEdited}
          onDone={() => setEditingId(null)}
          onCancel={cancelEdit}
        />
      );
    }
  }

  const cards = choices
    .filter((style) => style.id !== editingId)
    .map((style) => {
      const index = choices.indexOf(style);
      const shown = shownOf(style);
      return (
        <StyleCard
          key={style.id}
          style={shown}
          label={intl.formatMessage(messages.styleNumber, { number: index + 1 })}
          preview={previews.get(style.id)}
          isOn={style.id === selectedId}
          isDim={isEditing}
          isEdited={!sameStyle(shown, style)}
          disabled={busy || isEditing}
          onPick={() => setSelectedId(style.id)}
          onEdit={() => startEdit(shown)}
          onFamiliesChange={(effectFamilies) => switchFamilies({ ...shown, effectFamilies })}
        />
      );
    });

  let ownCard: React.ReactNode = null;
  if (own !== null && own.id !== editingId) {
    ownCard = (
      <StyleCard
        style={own}
        label={ownNumber}
        preview={video.style !== null && sameStyle(own, video.style) ? previews.get(own.id) : undefined}
        isOn={own.id === selectedId}
        isDim={isEditing}
        isEdited={false}
        disabled={busy || isEditing}
        onPick={() => setSelectedId(own.id)}
        onEdit={() => startEdit(own)}
        onFamiliesChange={(effectFamilies) => switchFamilies({ ...own, effectFamilies })}
      />
    );
  } else if (own === null && !isEditing) {
    ownCard = (
      <div className={clsx(styles.option, styles.optionOwn)}>
        <div className={clsx(styles.look, styles.lookPlaceholder)}>
          <span>{intl.formatMessage(messages.yourLook)}</span>
        </div>
        <div className={styles.optionHead}>
          <span className={styles.optionNumber}>{ownNumber}</span>
        </div>
        <div className={styles.optionTitle}>{intl.formatMessage(messages.writeOwn)}</div>
        <p className={styles.optionText}>{intl.formatMessage(messages.writeOwnText)}</p>
        <div className={styles.optionActions}>
          <SMLButton
            className={styles.small}
            disabled={busy}
            onClick={() => {
              const next = emptyOwnStyle();
              setOwn(next);
              startEdit(next);
            }}
          >
            {intl.formatMessage(messages.writeOwn)}
          </SMLButton>
        </div>
      </div>
    );
  }

  let body: React.ReactNode;
  if (isWorking) {
    body = <DirectorWorking kind="styles" withSkeletons />;
  } else {
    body = (
      <>
        <div className={clsx(styles.choices, isEditing ? styles.choicesOwn : styles.choicesFour)}>
          {editor}
          {cards}
          {ownCard}
        </div>
        {choices.length === 0 && !isEditing && (
          <div className={styles.work}>
            <p className={styles.workText}>{intl.formatMessage(messages.noStyles)}</p>
            <SMLButton variant="primary" disabled={busy || noRuns} onClick={() => writeAgain()}>
              {intl.formatMessage(messages.writeStyles)}
            </SMLButton>
          </div>
        )}
        {choices.length > 0 && !isEditing && (
          <AgainBox
            hint={intl.formatMessage(messages.stylesAgainHint)}
            button={intl.formatMessage(messages.stylesAgain)}
            disabled={busy || noRuns}
            onRun={(note) => writeAgain(note)}
          />
        )}
      </>
    );
  }

  return (
    <DirectedStepLayout side={<DirectedSummary />}>
      <div className={shell.head}>
        <h2>{intl.formatMessage(messages.styleTitle)}</h2>
        <p className={clsx(shell.sub, shell.subDesktop)}>
          {isEditing
            ? intl.formatMessage(messages.styleOwnIntro)
            : intl.formatMessage(messages.styleIntro, { story: video.story?.title ?? song.title })}
        </p>
      </div>
      <DirectorNotices kind="styles" error={action.error} showRunFailure={!isRevisit} onRetry={() => writeAgain()} />
      {body}
      {!isWorking && castFit !== null && castFit.fit !== 'good' && (
        <p className={clsx(styles.alert, styles.alertWarn)} role="status" data-testid="style-cast-fit">
          <span>
            {intl.formatMessage(
              castFit.fit === 'conflict' ? castDescriptionMessages.fitConflict : castDescriptionMessages.fitMixed
            )}
            {castFit.reason !== null && (
              <> {intl.formatMessage(castDescriptionMessages.fitReason, { reason: castFit.reason })}</>
            )}
          </span>
        </p>
      )}
      <div className={shell.foot}>
        <SMLButton
          className={clsx(styles.small, styles.ghost)}
          disabled={isWorking}
          onClick={() => openStep('stories')}
        >
          {intl.formatMessage(messages.backToStory)}
        </SMLButton>
        <span className={clsx(shell.grow, shell.desktopOnly)} />
        <span className={clsx(shell.footNote, shell.desktopOnly)}>{intl.formatMessage(messages.styleNext)}</span>
        <span className={styles.phoneOnly}>
          <SMLButton disabled={busy || noRuns || choices.length === 0} onClick={() => writeAgain()}>
            {intl.formatMessage(messages.stylesAgainPhone)}
          </SMLButton>
        </span>
        <SMLButton variant="primary" disabled={busy || !canUse} onClick={onUse}>
          {intl.formatMessage(messages.useStyle)}
        </SMLButton>
      </div>
      <ConfirmDialog
        isOpen={isAsking}
        tone="neutral"
        title={intl.formatMessage(messages.changeStyleTitle)}
        description={intl.formatMessage(messages.changeStyleText)}
        confirmLabel={intl.formatMessage(messages.changeStyleConfirm)}
        onConfirm={() => {
          applyStyle().catch(console.error);
        }}
        onCancel={() => setIsAsking(false)}
      />
    </DirectedStepLayout>
  );
};

const StyleChoices = TypedMemo(StyleChoicesComponent);

/**
 * Step 3, Style (design directed-video d1, d2): three ways to film the story, and "Write my own".
 * Each card shows the four effect families as chips; the director's choice is the default, and the
 * artist switches them on and off before the script is written.
 * "Use this style" sends `PUT …/style` (a style of the director, maybe edited, or the artist's own
 * with a new id), then `POST …/script`. While the director works, the step shows `DirectorWorking`.
 */
const StyleStepComponent: React.FC = () => {
  const { video } = useDirectedVideoContext();
  return <StyleChoices key={choicesKey(video.styles)} />;
};

export const StyleStep = TypedMemo(StyleStepComponent);
